import React, { useCallback, useEffect, useRef, useState } from 'react';

import { LeftIcon, RightIcon } from '@travel/ui/components/NavigationButton/navigationIcon';
import cx from '@travel/ui/utils/classnames';

import { isTouchDevice } from 'store/utils';

import { DIRECTION } from 'constants/direction';
import useOnResizeDebounce from 'hooks/useOnResizeDebounce';

import styles from './horizontalNavScroll.module.scss';

type HorizontalNavScrollProps = {
  children: React.ReactNode;
  onScroll?: (element: HTMLDivElement | HTMLElement) => void;
  scrollableAmount?: number;
  wrapperClass?: string;
  buttonClass?: string;
  contentNavClass?: string;
  arrowContainerClass?: string;
};

/* The amount of scroll to the element (left/right) */
const DEFAULT_SCROLL_AMOUNT = 3; // For non touch device (hover event)
const DEFAULT_SCROLL_AMOUNT_TOUCH_DEVICE = 30; // For touch device (touch event)

const SCROLL_INTERVAL_DELAY = 10; // Delay between the scroll position (lower value -> fast scroll, higher value -> slow scroll, preferred value 10-20 for better behavior)

const HorizontalNavScroll: React.FC<React.PropsWithChildren<HorizontalNavScrollProps>> = ({
  children,
  onScroll,
  wrapperClass,
  buttonClass,
  contentNavClass,
  scrollableAmount,
  arrowContainerClass,
}) => {
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);

  const handleScroll = useCallback(() => {
    const nav = scrollContentRef.current;
    if (nav) {
      setShowLeftArrow(nav.scrollLeft > 0);
      setShowRightArrow(nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 1);
      onScroll && onScroll(nav);
    }
  }, [onScroll]);

  const handleMouseEnter = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>,
      direction: DIRECTION,
      scrollAmount: number = DEFAULT_SCROLL_AMOUNT,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      const intervalId: NodeJS.Timeout = setInterval(() => {
        const nav: HTMLElement | null = scrollContentRef.current;
        if (nav) {
          if (direction === DIRECTION.LEFT) {
            nav.scrollLeft -= scrollAmount;
          } else {
            nav.scrollLeft += scrollAmount;
          }
        }
      }, SCROLL_INTERVAL_DELAY);
      scrollIntervalRef.current = intervalId;
      handleScroll();
    },
    [handleScroll],
  );

  const handleMouseLeave = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      handleScroll();
    },
    [handleScroll],
  );

  useEffect(() => {
    handleScroll();
  }, [children, handleScroll]);

  useEffect(() => {
    const nav = scrollContentRef.current;
    if (nav) {
      nav.addEventListener('scroll', handleScroll);
      return () => {
        nav.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  useOnResizeDebounce(handleScroll);

  return (
    <div className={cx(styles.scrollSection, wrapperClass)}>
      <div className={cx(styles.arrowContainer, arrowContainerClass, showLeftArrow && styles.show)}>
        <button
          data-testid="left-nav-button"
          className={buttonClass}
          tabIndex={-1}
          onMouseEnter={!isTouchDevice ? (e) => handleMouseEnter(e, DIRECTION.LEFT) : undefined}
          onMouseLeave={!isTouchDevice ? handleMouseLeave : undefined}
          onTouchStart={
            isTouchDevice
              ? (e) =>
                  handleMouseEnter(
                    e,
                    DIRECTION.LEFT,
                    scrollableAmount || DEFAULT_SCROLL_AMOUNT_TOUCH_DEVICE,
                  )
              : undefined
          }
          onTouchEnd={isTouchDevice ? handleMouseLeave : undefined}
          type="button"
        >
          <LeftIcon />
        </button>
      </div>
      <div
        ref={scrollContentRef}
        data-testid="scrollable-content"
        className={cx(styles.contentNav, contentNavClass)}
      >
        {children}
      </div>
      <div
        className={cx(styles.arrowContainer, arrowContainerClass, showRightArrow && styles.show)}
      >
        <button
          data-testid="right-nav-button"
          className={buttonClass}
          tabIndex={-1}
          onMouseEnter={!isTouchDevice ? (e) => handleMouseEnter(e, DIRECTION.RIGHT) : undefined}
          onMouseLeave={!isTouchDevice ? handleMouseLeave : undefined}
          onTouchStart={
            isTouchDevice
              ? (e) =>
                  handleMouseEnter(
                    e,
                    DIRECTION.RIGHT,
                    scrollableAmount || DEFAULT_SCROLL_AMOUNT_TOUCH_DEVICE,
                  )
              : undefined
          }
          onTouchEnd={isTouchDevice ? handleMouseLeave : undefined}
          type="button"
        >
          <RightIcon />
        </button>
      </div>
    </div>
  );
};

export default HorizontalNavScroll;
